import { useMemo, useState } from 'react';
import { Plus, Search } from 'lucide-react';
import { useI18n } from '../../context/useI18n';
import { collectOrgLicenses, isTrainerActive, orgDisplayName } from '../../utils/organizationView';
import { findOrgOperator, summarizeOperatorSubscription } from '../../utils/organizationLicenseSummary';
import CreateCoachOrgModal from './CreateCoachOrgModal';
import ManageCoachOrgModal from './ManageCoachOrgModal';
import InviteCodeCell from './InviteCodeCell';

function LicenseChips({ operator }) {
  const licenses = collectOrgLicenses(operator);
  if (!licenses.length) return <span className="text-xs text-stone-400">Sin licencias</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {licenses.map((lic) => (
        <span key={`${lic.label}-${lic.code}`} className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold bg-violet-100 text-violet-900">
          {lic.label}
        </span>
      ))}
    </div>
  );
}

export default function OrganizationsCoachList({
  trainers = [],
  users = [],
  loading = false,
  onSaveInviteCode,
  onManageLicenses,
  onReload,
  onOpenCreate,
}) {
  const { t } = useI18n();
  const [search, setSearch] = useState('');
  const [manageTarget, setManageTarget] = useState(null);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return trainers
      .filter((tr) => isTrainerActive(tr))
      .filter((tr) => {
        if (!q) return true;
        const name = orgDisplayName(tr) || tr.nombre || '';
        return [name, tr.email, tr.invite_code].filter(Boolean).join(' ').toLowerCase().includes(q);
      })
      .map((tr) => {
        const operator = findOrgOperator(users, { trainerId: tr.id });
        const sub = summarizeOperatorSubscription(operator);
        return { trainer: tr, operator, sub, name: orgDisplayName(tr) || tr.nombre };
      });
  }, [trainers, users, search]);

  const handleManage = (trainer) => {
    const operator = findOrgOperator(users, { trainerId: trainer.id });
    setManageTarget({ trainer, operator, orgName: orgDisplayName(trainer) || trainer.nombre });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-sm text-stone-600">
          {t('orgs.coach_list_hint', 'Organizaciones coach con marca, licencias y administrador gestionables desde aquí.')}
        </p>
        <button type="button" className="btn-primary text-sm inline-flex items-center gap-2 shrink-0" onClick={() => onOpenCreate?.()}>
          <Plus className="w-4 h-4" />
          {t('orgs.create_coach', 'Crear organización coach')}
        </button>
      </div>

      <div className="relative max-w-sm">
        <Search className="w-4 h-4 text-stone-500 absolute left-2 top-2.5" />
        <input className="input pl-7 w-full" placeholder={t('common.search', 'Buscar')} value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto ui-light-panel relative">
        {loading ? (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70 rounded-2xl">
            <p className="text-sm text-stone-500">{t('common.loading', 'Cargando...')}</p>
          </div>
        ) : null}
        <table className="min-w-full text-sm">
          <thead className="bg-stone-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase">{t('orgs.organization', 'Organización')}</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase">{t('orgs.licenses', 'Licencias')}</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase">{t('common.status', 'Estado')}</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase">{t('users.invite_code', 'Código')}</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase">{t('common.actions', 'Acciones')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-stone-500">{t('orgs.no_coaches', 'Sin coaches registrados.')}</td></tr>
            ) : rows.map(({ trainer, operator, sub, name }) => (
              <tr key={trainer.id} className="hover:bg-stone-50">
                <td className="px-4 py-3">
                  <p className="font-medium text-stone-900">{name}</p>
                  <p className="text-xs text-stone-500">{trainer.email}</p>
                  <p className="text-xs text-stone-400 mt-0.5">{t('orgs.type.coach', 'Coach')}</p>
                </td>
                <td className="px-4 py-3"><LicenseChips operator={operator} /></td>
                <td className="px-4 py-3">
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize ${sub.active ? 'bg-emerald-100 text-emerald-800' : 'bg-stone-100 text-stone-700'}`}>
                    {sub.estado || '—'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <InviteCodeCell value={trainer.invite_code} onSave={(code) => onSaveInviteCode?.(trainer.id, code)} />
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-2">
                    <button type="button" className="btn-secondary text-xs" onClick={() => handleManage(trainer)}>
                      {t('orgs.manage', 'Gestionar')}
                    </button>
                    {operator && onManageLicenses ? (
                      <button
                        type="button"
                        className="btn-secondary text-xs"
                        onClick={() => onManageLicenses({
                          operator,
                          orgName: name,
                        })}
                      >
                        {t('orgs.licenses', 'Licencias')}
                      </button>
                    ) : null}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ManageCoachOrgModal
        trainer={manageTarget?.trainer}
        operator={manageTarget?.operator}
        open={Boolean(manageTarget)}
        onClose={() => setManageTarget(null)}
        onSaved={() => onReload?.()}
        onArchived={() => {
          setManageTarget(null);
          onReload?.();
        }}
        onManageLicenses={(target) => {
          setManageTarget(null);
          onManageLicenses?.(target);
        }}
      />
    </div>
  );
}
